// ConnectModal — an admin's share sheet for inviting someone onto a host via
// SSO: structured facts about the host (policy, slots remaining) up top,
// then a single ready-to-send message — worded for the RECIPIENT, not the
// admin — bundling the setup guide link (/connect, ui/src/pages/Connect.tsx),
// Enrollment URL, and Dev host. Used to be three separate copyable fields
// the admin had to assemble into their own message by hand; one message the
// admin can paste as-is is the whole point now (2026-09-10). Self-hosted
// deployments require Dev host — see Connector's own "leave blank for a
// BlueRock-managed sandbox" caption, which does not apply to this product.
// Design rationale: docs/superpowers/specs/2026-09-10-sso-enrollment-distribution-design.md.
import { useState } from "react";
import { Modal, Tooltip, Button } from "antd";
import { Copy, Check, IdCard } from "lucide-react";
import { PolicyChip } from "@/components/fleet/PolicyChip";

const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

// The enrollment URL is the source of truth for both the CA's identity host
// and its full fingerprint (https://<identity host>#fp=<fingerprint>) —
// derived here rather than trusted from separate API fields, because
// /api/fleet's own `ca.fp` field comes back PRE-TRUNCATED by the backend
// ("a304e12d02a0…", 13 chars, ellipsis baked into the string itself) while
// the fingerprint embedded in bootstrap_url is the real, full 64-char hash
// (confirmed against the live demo API, 2026-09-10). Used by both this
// modal and ManagementHostCard so neither trusts the truncated field.
export function parseEnrollmentUrl(bootstrapUrl: string): { identityHost: string; fingerprint: string } {
  try {
    const u = new URL(bootstrapUrl);
    return { identityHost: u.host, fingerprint: new URLSearchParams(u.hash.replace(/^#/, "")).get("fp") || "" };
  } catch {
    return { identityHost: "", fingerprint: "" };
  }
}

export interface ConnectInfo {
  ssh_user?: string;
  ssh_host?: string;
  bootstrap_url?: string;
  provisioner?: string;
  login?: string;
  client_hint?: string;
}
export interface ConnectHost {
  name: string;
  publicIp?: string;
  role?: string;
  /** This host's one enforced policy (until 2.7) — shown as a chip in the
   *  metadata row, which is the whole reason this modal is host-scoped at all. */
  policyTier?: string;
  /** Total sandbox slots this host supports, and how many are currently in
   *  use — shown as "N of M free" in the metadata row so an admin can see
   *  whether there's room before inviting someone, without leaving this
   *  share sheet. Both undefined in the (now-unused) fleet-wide mode. */
  sandboxCapacity?: number;
  sandboxesUsed?: number;
}

function Copyable({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Tooltip title={done ? "Copied" : "Copy"}>
      <button
        aria-label={label ? `Copy ${label}` : "Copy"}
        onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1200); }); }}
        className="inline-flex flex-none items-center rounded p-1 text-text-muted transition-colors hover:text-accent"
      >
        {done ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </Tooltip>
  );
}

export default function ConnectModal({
  open, onClose, connect, hosts, focusHost,
}: {
  open: boolean;
  onClose: () => void;
  connect: ConnectInfo;
  hosts: ConnectHost[];
  /** When set, scopes the modal to one host: the metadata row (host name,
   *  policy, slots remaining) renders, the primary SSH target becomes that
   *  host's own address instead of the fleet's shared placement endpoint,
   *  and the generated message names it by name. */
  focusHost?: ConnectHost | null;
}) {
  const user = connect.ssh_user || "bluedev";
  // The SSH target must always be a ROUTABLE address, never a bare host label
  // (a name like "brdemo-01" isn't DNS-resolvable and is exactly what breaks a
  // developer's first connect). Two topologies:
  //   • per-worker public IP (AWS): each worker is reachable directly → use its own IP.
  //   • front-door (brdemo): workers are private; developers SSH to the shared
  //     public endpoint and placement routes them → fall back to connect.ssh_host.
  // The host NAME is only a last-resort label and never the primary target.
  const host = focusHost
    ? (focusHost.publicIp || connect.ssh_host || focusHost.name)
    : connect.ssh_host || hosts.find((h) => h.role !== "control-plane")?.publicIp || hosts[0]?.publicIp || "";
  const boot = connect.bootstrap_url || "";
  const configured = !!boot && !!host;

  // The static setup guide (ui/src/pages/Connect.tsx) carries no host data of
  // its own — it's folded into the message below as-is. One link works for
  // every host, so it's never regenerated or host-scoped.
  const connectLink = `${window.location.origin}/connect`;
  // Worded AT the recipient (not a data dump for the admin to relay) — the
  // admin's job is now just "paste this", not "assemble a message from three
  // fields". SSH user only appears when it's not the Connector default,
  // same condition the old standalone caption used.
  const message = `You have been invited to connect your AI coding tools to a BlueRock sandbox.

1. To get started, open the setup guide and follow the steps:

${connectLink}

2. When prompted, enter the following:

Enrollment URL
${boot}

Dev host
${host}${user !== "bluedev" ? `

SSH user
${user} (override under Advanced — Connector defaults to bluedev)` : ""}`;

  const slotsRemaining =
    focusHost?.sandboxCapacity != null && focusHost?.sandboxesUsed != null
      ? focusHost.sandboxCapacity - focusHost.sandboxesUsed
      : undefined;

  // Redundant with the small copy icon on the Message card's own header —
  // deliberately so. A full-width button at the bottom of the modal is a
  // bigger, easier target once the admin's read through the message and is
  // ready to act, without making them scroll back up to the card header.
  const [msgCopied, setMsgCopied] = useState(false);
  const copyMessage = () => {
    navigator.clipboard?.writeText(message).then(() => {
      setMsgCopied(true);
      setTimeout(() => setMsgCopied(false), 1200);
    });
  };

  return (
    <Modal open={open} onCancel={onClose} width={640} footer={null}
      title={
        <span className="inline-flex items-center gap-2">
          <IdCard size={17} /> Invite via SSO
        </span>
      }>
      {!configured ? (
        <div className="rounded-lg border border-border bg-surface-elev p-4 text-sm text-text-secondary">
          SSO isn’t activated yet — no enrollment URL is published. Configure and activate SSO
          (Fleet → the SSO chip) to expose a cobalt enrollment URL here.
        </div>
      ) : (
        <>
          {focusHost && (
            <div className="mb-3 grid grid-cols-3 gap-4 border-b border-border pb-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Host</div>
                <div className="text-[13px] text-text-primary" style={{ fontFamily: MONO }}>{focusHost.name}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Policy</div>
                <div className="mt-0.5">
                  {focusHost.policyTier ? <PolicyChip tier={focusHost.policyTier} linkable={false} /> : <span className="text-[13px] text-text-muted">—</span>}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Slots remaining</div>
                <div className="text-[13px] text-text-primary">
                  {slotsRemaining != null ? `${slotsRemaining} of ${focusHost.sandboxCapacity} free` : "—"}
                </div>
              </div>
            </div>
          )}

          <p className="mb-3 text-sm text-text-secondary">
            Send these details to the users you wish to invite.
            {slotsRemaining != null &&
              ` There ${slotsRemaining === 1 ? "is" : "are"} ${slotsRemaining} slot${slotsRemaining === 1 ? "" : "s"} remaining on this host.`}
          </p>

          <div className="rounded-xl border border-border bg-surface-raised">
            <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Message</span>
              <Copyable text={message} label="Message" />
            </div>
            <div className="whitespace-pre-wrap px-3 py-2.5 text-sm leading-relaxed text-text-primary">{message}</div>
          </div>

          <Button type="primary" block className="mt-3" icon={msgCopied ? <Check size={14} /> : <Copy size={14} />} onClick={copyMessage}>
            {msgCopied ? "Copied" : "Copy message"}
          </Button>
        </>
      )}
    </Modal>
  );
}
